
import Vue from "vue";
import CodeMirrorWrapper from "../3rdPartyWrappers/CodeMirrorWrapper.vue";
import { Component, Prop } from "vue-property-decorator";
import IconButton from "@/components/UI/IconButton.vue";
import CodeInputDialog from "@/components/Reusable/CustomInputs/CodeInput/CodeInputDialog.vue";
import { Tag } from "@/types/tags";
import Icon from "@/components/UI/Icon.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import CodeBoxButtonForCodeEditor from "@/components/Partners/CodeBoxButtonForCodeEditor.vue";
import { formatCSS, formatHTML } from "@/lib/code-editor/code-formatter";
import NotificationLogger from "@/lib/notifications/notifications";

export type CodeInputLanguage =
  | "css"
  | "html"
  | "javascript"
  | "x-php" // PHP with wrapper tags (e.g. <?php)
  | "php" // Plain PHP without wrapper tags (e.g. <?php)
  | "twig" // Vanilla Twig
  | "twig/html" // Mixed Twig (HTML + Twig)
  | "twig/css"; // Mixed Twig (CSS + Twig)

@Component({
  components: {
    Icon,
    CodeInputDialog,
    IconButton,
    CodeMirrorWrapper,
    NiceButton,
    CodeBoxButtonForCodeEditor
  }
})
export default class CodeInput extends Vue {
  @Prop()
  value!: string | null;

  @Prop()
  autofillOnEmpty!: string | null;

  @Prop()
  language!: CodeInputLanguage;

  @Prop({ default: true })
  inline!: boolean;

  @Prop({ default: true })
  fullscreen!: boolean;

  @Prop({ default: "" })
  placeholder!: string;

  @Prop({
    default() {
      return [];
    }
  })
  tags!: Tag[];

  @Prop({
    default() {
      return [];
    }
  })
  twigVariables!: string[];

  dialog = false;
  isFormatting = false;

  @Prop({ default: null })
  focusAtPosition!: number | null;

  cursorPosition: number | null = null;

  @Prop({ default: false })
  showLineNumbers!: boolean;

  get formattable() {
    return this.language === "css" || this.language === "html";
  }

  get title() {
    let lang;
    switch (this.language) {
      case "x-php":
        lang = "PHP";
        break;
      case "html":
        lang = "HTML";
        break;
      case "javascript":
        lang = "JavaScript";
        break;
      case "css":
        lang = "CSS";
        break;
      default:
        lang = this.language;
    }

    return `Open ${lang} Editor`;
  }

  closeDialog() {
    this.dialog = false;
  }

  edit(newVal: string | null) {
    if (!newVal) {
      this.$emit("input", null);
      return;
    }

    this.$emit("input", newVal);
  }

  onCursorPosition(position: number) {
    this.cursorPosition = position;
  }

  async onFormat() {
    if (!this.value) return;
    if (this.isFormatting) return;

    this.isFormatting = true;

    try {
      if (this.language === "css") {
        const formattedCSS = await formatCSS(this.value);
        this.edit(formattedCSS);
      } else if (this.language === "html") {
        const formattedHTML = await formatHTML(this.value);
        this.edit(formattedHTML);
      }

      this.isFormatting = false;
    } catch (error) {
      const languageName = this.language.toUpperCase();
      NotificationLogger.log.errorMessage(
        `Unable to format ${languageName} code. Please check for syntax errors like missing brackets, quotes, or semicolons, then try formatting again.`
      );
      console.error(error);
    } finally {
      this.isFormatting = false;
    }
  }
}
